import moment from 'moment-timezone';
import numeral from 'numeral';
import { intervalToDuration } from "date-fns";
import _ from "lodash";
import {DEFAULT_FILTERS} from "../components/user/riaPowerSearch/CustomFilters";

export const ARISTOTLE_ID = 4134
export const STATE_FARM_ID = 4341
export const HUNTINGTON_ID = 4718

export const shouldRedactGender = (teamId) => teamId === HUNTINGTON_ID
export const shouldRedactAge = (teamId) => teamId === STATE_FARM_ID || teamId === HUNTINGTON_ID

export function flattenArray(value) {
  if (!Array.isArray(value)) {
    return value;
  }

  while (Array.isArray(value) && value.length === 1 && Array.isArray(value[0])) {
    value = value[0];
  }

  return value;
}

export const translateToggleFilter = (item) => {
  let result
  if (item.toLowerCase() === 'hq') {
    result = 'HQ'
  } else {
    result = toTitleCase(item)
  }
  return result
}

export const checkLocationLabelsInContainer = (containerId) => {
  const repSpecialLabels = ['_Mile Radius', 'Branch Office IDs']
  const firmSpecialLabels = ['Mile Radius', 'U.S. Region', 'Zip code', 'Private Residence']
  return {
    hasRepLocations: checkAnyValueInContainer(DEFAULT_FILTERS['contact_location'].filters.map(el => el.name).map(el => el.replace('Branch Office ', '_')).concat(repSpecialLabels), containerId),
    hasFirmLocations: checkAnyValueInContainer(DEFAULT_FILTERS['firm_location'].filters.map(el => el.name).concat(firmSpecialLabels), containerId)
  }
}

// Contact & Rep Location's own filter value keys (ContactsLocation.jsx fieldMappings),
// excluding rep_primary_location itself and its display-only selected*/exclude* mirrors.
export const CONTACT_LOCATION_VALUE_KEYS = [
  'contact_currently_company_branches_city',
  'contact_currently_company_branches_country',
  'contact_currently_company_branches_metro_area',
  'contact_currently_company_branches_state',
  'contact_currently_company_branches_counties',
  'contact_currently_company_branches_street',
  'contact_currently_company_branches_zip',
  'contact_currently_company_branch_office_ids',
  'branch_us_regions',
  'branch_location',
]

// A location "option" param (e.g. rep_primary_location) only modifies how its group's
// location filters get applied — persisting it with nothing left in the group makes the
// backend treat the search as location-scoped for nothing. Drop it (and any side-keys,
// e.g. branch_radius) once no value key in the group is present. Dropping instead of
// nulling also keeps it out of saved-search fingerprints. GEN-3225.
export const dropOrphanLocationOption = (params, optionKey, valueKeys, alsoDropKeys = []) => {
  const hasAnyValue = valueKeys.some(key => {
    const value = params?.[key];
    return Array.isArray(value) ? value.length > 0 : !_.isNil(value) && value !== '';
  });
  if (hasAnyValue) return params;

  const next = {...params};
  delete next[optionKey];
  alsoDropKeys.forEach(key => delete next[key]);
  return next;
}

const checkAnyValueInContainer = (values, containerId) => {
  const container = document.getElementById(containerId);
  if (!container) return false;
  const innerText = container.innerText || "";
  return values.some(value => {
    const startsWithUnderscore = value.startsWith('_');
    const regex = startsWithUnderscore
      ? new RegExp(`(^|\\n|[^\\s])${value}:`)
      : new RegExp(`(^|\\n|[^\\s_])${value}:`);
    return regex.test(innerText);
  });
};

export const isMultiRow = (containerId) => {
  const container = document.getElementById(containerId);
  if (!container) return false;
  const children = Array.from(container.children);
  if (children.length <= 1) return false;
  const firstOffset = children[0].offsetTop;
  return children.some(child => child.offsetTop !== firstOffset);
};

export const searchParamsKeysTranslator = (searchParams) => {
  return {
    score: searchParams['scoreRange'] || searchParams['score'],
    ma_score: searchParams['maScoreRange'] || searchParams['ma_score'],
    licensed_agents_of_an_insurance_company: searchParams['numOfEmployeesAreLicInsuranceAgent'] || searchParams['licensed_agents_of_an_insurance_company'],
    reg_state_securities_authorities_investment_adviser_reps: searchParams['numOfEmployeesAreState'] || searchParams['reg_state_securities_authorities_investment_adviser_reps'],
    employee_perform_investment_advisory_functions_and_research: searchParams['numOfEmployeesAreAdvisor'] || searchParams['employee_perform_investment_advisory_functions_and_research'],
    employee_registered_as_a_broker_dealer: searchParams['numOfEmployeesAreBroker'] || searchParams['employee_registered_as_a_broker_dealer'],
    num_of_cliens_had_financial_planning_services_last_fiscal_yr: searchParams['numOfCliensHadFinancialPlanningServicesLastFiscalYr'] || searchParams['num_of_cliens_had_financial_planning_services_last_fiscal_yr'],
  }
}

export function scrollInsideContainer(containerId, targetId) {
  const customAdjustment = {
    'ownership_table': -10,
    'current_client_breakdown': 122,
    'ai_top_container': -20,
  }
  const container = document.getElementById(containerId)
  const target = document.getElementById(targetId)
  if (container && target) {
    const containerRect = container.getBoundingClientRect()
    const targetRect = target.getBoundingClientRect()
    const scrollUp = targetRect.top - containerRect.top;
    const scrollDown = targetRect.bottom - containerRect.bottom;
    if (Math.abs(scrollUp) < Math.abs(scrollDown)) {
      container.scrollTop += (scrollUp + (customAdjustment[targetId] || 0));
    } else {
      container.scrollTop += (scrollDown + (customAdjustment[targetId] || 0));
    }
    return 'ok'
  } else {
    return 'retry'
  }
}

function generatePermutation(permutation, words) {
  let output = words[0];
  let index = 0;
  for (const word of words.slice(1)) {
    output = output + (permutation[index] ? '-' : ' ') + word;
    index += 1;
  }
  return output;
}

export const capAdder = (label = "") => {
    let newLabel = label;

    if (!label || label.includes("Cap")) return label;

    if (label.includes("Small/Mid")) {
        newLabel = label.replace("Small/Mid", "Small/Mid Cap");
    } else {
        newLabel = label
            .replace("Small", "Small Cap")
            .replace("Large", "Large Cap");
    }

    return newLabel;
};

// Helper function to sort quarters chronologically
export const sortQuarters = (quarters) => {
    return quarters.sort((a, b) => {
        const [quarterA, yearA] = a.quarter.split(' ');
        const [quarterB, yearB] = b.quarter.split(' ');

        const yearDiff = parseInt(yearA) - parseInt(yearB);
        if (yearDiff !== 0) return yearDiff;

        const qNumA = parseInt(quarterA.replace('Q', ''));
        const qNumB = parseInt(quarterB.replace('Q', ''));
        return qNumA - qNumB;
    });
};

export const sortTickerMap = (ticker_map, formatted) => {
    return Object.entries(ticker_map)
        .sort(([tickerA], [tickerB]) => {
            const valuesA = formatted[tickerA] || [];
            const valuesB = formatted[tickerB] || [];

            const hasDataA = Array.isArray(valuesA) && valuesA.some(p => p && p.amount != null && p.amount !== 0);
            const hasDataB = Array.isArray(valuesB) && valuesB.some(p => p && p.amount != null && p.amount !== 0);

            // Tickers with data come first
            if (hasDataA && !hasDataB) return -1;
            if (!hasDataA && hasDataB) return 1;
            return 0;
        })
        .reduce((acc, [ticker, value]) => {
            acc[ticker] = value;
            return acc;
        }, {});
}

export const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const equalsCaseInsensitive = (a, b) =>
  a != null && b != null && a.toLowerCase() === b.toLowerCase();

export const includesCaseInsensitive = (values, value) =>
  values.some((v) => equalsCaseInsensitive(v, value));

export const formatDate = ((date) =>
  moment(date).tz('America/New_York').format('MM-DD-YY h:mm a')
)

export const formatDateUS = ((date, timezone = 'America/New_York') =>
  moment(date).tz(timezone).format('MM/DD/YYYY h:mm a')
)

export const formatDateWith = ((date, format, timezone = 'America/New_York') =>
  moment(date).tz(timezone).format(format)
)

export const formatDateWithoutTimezone = ((date, format, timezone = 'America/New_York') =>
  moment(date).format(format)
)

export const dateUSFormat = ((date, timezone = 'America/New_York') =>
  moment(date).tz(timezone).format('MM/DD/YYYY')
)

export const dateFormat = (date) => moment(date).format('MM/DD/YYYY');
export const monthYearFormat = (date) => moment(date).format('MM/YYYY');

export const toUTCNoon = (year, month, day) =>
    new Date(Date.UTC(year, month, day, 12, 0, 0));

export const formatDateBostonYMD = (date) => {
    return moment(date).tz('America/New_York').format('YYYY-MM-DD');
}

export const formatMoney = ((money) =>
  numeral(money).format('$ 0,0')
)

export const formatNumber = ((number) =>
  numeral(number).format('0,0')
)

export const formatDateShort = (date) => {
  const options = { month: 'short', day: 'numeric' };
  return new Intl.DateTimeFormat('en-US', options).format(new Date(date));
}

export const formatDateDMY = (date, timezone = null) => {
  if (timezone) return moment(date).tz(timezone).format('DD MMM YYYY')
  return moment(date).format('DD MMM YYYY')
}

export const formatDateMDY = (date, timezone) => {
  timezone = 'America/New_York';
  return moment(date).tz(timezone).format('MMM D YYYY')
}

/**
 * Converts a JS Date object to a plain YYYY-MM-DD string using the browser's
 * local date. This avoids timezone offset issues when serializing dates for the
 * backend (e.g., a Date representing March 1st midnight in the browser's local
 * timezone would serialize to "2026-02-28T23:00:00.000Z" in UTC via JSON,
 * causing the backend to parse it as Feb 28).
 */
export const toLocalDateString = (date) => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Inverse of toLocalDateString: parses a plain "YYYY-MM-DD" string back into a
 * Date at local midnight. The native Date constructor parses date-only ISO
 * strings as UTC midnight, which rolls back a day once formatted in any
 * timezone behind UTC — this reads the parts and builds the Date from local
 * components instead. Date instances (and anything else) pass through as-is.
 */
export const parseLocalDate = (value) => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (match) {
      const [, year, month, day] = match;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
  }
  return new Date(value);
}

export const formatMonthYear = (date) => {
    if (!date) return '';

    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();

    return `${month}/${year}`;
};

export const formatDateMDYWithoutTimeZone = (date, format = 'MMM D YYYY') => {
  if (!date) return '';
  return moment(date).format(format)
}

export const compactAndUniq = (array) => {
  const arrayFlat = array.flat()
  const uniqueItems = Array.from(new Set(arrayFlat.map(item => item && JSON.stringify(item))))
    .map(item => item && JSON.parse(item));
  return [...new Set(uniqueItems.filter(Boolean))].flat();
}
export const formatDateMDYWithoutTimeZoneUTC = (date, format = 'MMM D YYYY') => {
  return moment(date).tz('UTC').format(format)
}

export const formatDateMY = (date, timezone) => {
  timezone = 'America/New_York';
  return moment(date).tz(timezone).format('MMM YYYY')
}

export const getMonthName = (monthNumber) => {
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  return monthNames[monthNumber - 1];
}

export const formatDateY = (date, timezone) => {
  return moment(date).format('YYYY')
}

export const formatDateDMYHCommaSeparated = (date, timezone) => {
  timezone = 'America/New_York';
  return moment(date).tz(timezone).format('DD MMM YYYY, hh:mm a')
}

export const formatDateDMYH = (date, timezone) => {
  timezone = 'America/New_York';
  return moment(date).tz(timezone).format('DD MMM YYYY hh:mm a')
}

export const formatDateMDYHCommaSeparated = (date, timezone) => {
  timezone = 'America/New_York';
  return moment(date).tz(timezone).format('MMM DD, YYYY hh:mm a')
}

export const capitalize = (s) => {
  if (typeof s !== 'string') return ''
  return s.toLowerCase().replace(/(?:^|\s|-)\S/g, string => string.toUpperCase())
}

export const greaterThanDay = (value, day) => {
  const toCompare = moment().add(day, 'days');
  const date = moment(value)
  return date.isBefore(toCompare)
}

export const formattedNewsDate = (date) => {
  let formattedDate = '';
  date = moment(date)
  const dateComparisson = moment().subtract(2, 'months');

  if (date.toDate() > dateComparisson.toDate()) {
    formattedDate = date.from(moment());
  } else {
    formattedDate = formatDateDMY(date);
  }

  return formattedDate;
}

export function getDateAgoByDay(date) {
  const datetime = moment.utc(date).local()
  return datetime.fromNow();
}

export const getCustomTimeAgo = (date, ago=' ago') => {
  const now = moment.utc();
  const inputDate = moment.utc(date);
  
  const months = now.diff(inputDate, 'months');
  if (months > 0) {
    return `${months}mo${ago}`;
  }
  
  const days = now.diff(inputDate, 'days');
  if (days > 0) {
    return `${days}d${ago}`;
  }
  
  const hours = now.diff(inputDate, 'hours');
  if (hours > 0) {
    return `${hours}h${ago}`;
  }
  
  const minutes = now.diff(inputDate, 'minutes');
  if (minutes > 0) {
    return `${minutes}m${ago}`;
  }
  
  const seconds = now.diff(inputDate, 'seconds');
  if (seconds > 0) {
    return `${seconds}s${ago}`;
  }

  return 'now';
};

export const getSortedHash = (inputHash) => {
  var resultHash = {};

  var keys = Object.keys(inputHash);
  keys.sort(function (a, b) {
    return inputHash[a] - inputHash[b]
  }).reverse().forEach(function (k) {
    resultHash[k] = inputHash[k];
  });
  return resultHash;
}

export const camelToSnake = (string) => {
  return string.replace(/[\w]([A-Z])/g, function (m) {
    return m[0] + "_" + m[1];
  }).toLowerCase();
}

export const snakeToTitleCase = (s) => {
  return s.replace(/^[-_]*(.)/, (_, c) => c.toUpperCase()).replace(/[-_]+(.)/g, (_, c) => ' ' + c.toUpperCase())
}

export const stringToSnakeCase = (str) => {
  return str && str.match(
    /[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
    .map(s => s.toLowerCase())
    .join('_');
}

export const blockCopy = () => {
  let items = document.getElementsByClassName('bc');

  if (items) {
    const length = items.length

    for (let i = 0; i < length; i++) {
      let item = items[i]
      item.addEventListener('copy', function (event) { event.preventDefault() })
      item.addEventListener('selectstart', function (event) { event.preventDefault() })
    }
  }
}

export const fromArrayToSentence = (array) => {
  if (array.length > 1) {
    return array.slice(0, -1).join(', ') + ' and ' + array.slice(-1);
  } else if (array.length > 0) {
    return array[0]
  }
  return ''
}

export const fromArrayToOrSentence = (array) => {
  if (array.length > 1) {
    return array.slice(0, -1).join(', ') + ' or ' + array.slice(-1);
  } else if (array.length > 0) {
    return array[0]
  }
  return ''
}

export const fromArrayToConcatenation = (array) => {
  if (array.length > 1) {
    return array.join(', ');
  } else if (array.length > 0) {
    return array[0]
  }
  return '-'
}

export function toTitleCase(str) {
  if (str && typeof str === 'string') {
    return str.replace(/\w\S*/g, function (txt) {
      if (txt.toLowerCase() === 'ria') {
        return 'RIA';
      } else {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
      }
    });
  } else {
    return str;
  }
}

export function toCustomTitleCaseAddress(str) {
    if (str && typeof str === 'string') {
        const words = str.split(' ');

        const titleCaseWordsAddress = words.map(word => {
            const alphabeticPart = word.replace(/[^a-zA-Z]/g, '');
            const punctuation = word.replace(/[a-zA-Z]/g, '');

            if (/\d/.test(word)) {
                return word;
            }

            if (!/[a-zA-Z]/.test(word)) {
                return word;
            }

            if (alphabeticPart.length === 2 && uspsStates.includes(alphabeticPart.toUpperCase())) {
                return alphabeticPart.toUpperCase() + punctuation;
            }

            if (alphabeticPart.toLowerCase() === 'of') {
                return 'of' + punctuation;
            }

            return alphabeticPart.charAt(0).toUpperCase() + alphabeticPart.slice(1).toLowerCase() + punctuation;
        });

        return titleCaseWordsAddress.join(' ').split('-').map((el, i) => {
            if (i > 0) {
                const parts = el.split(' ');
                parts[0] = parts[0].charAt(0).toUpperCase() + parts[0].slice(1).toLowerCase();
                return parts.join(' ');
            } else {
                return el
            }
        }).join('-')
    } else {
        return str;
    }
}

export function toCustomTitleCase(str) {
  if (str && typeof str === 'string') {
    const words = str.split(' ');

    const titleCaseWords = words.map(word => {
      if (word.toLowerCase() === 'of') {
        return 'of'
      } else if (uspsStates.includes(word.toUpperCase())) {
        return word.toUpperCase()
      } else if (/^[a-zA-Z]+$/.test(word)) {
        const allUppercase = word === word.toUpperCase();
        if (allUppercase) {
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        } else {
          const hasMidUppercase = /[a-z].*[A-Z]/.test(word);
          if (hasMidUppercase) {
            return word;
          } else {
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
          }
        }
      } else {
        return word;
      }
    });

    return titleCaseWords.join(' ').split('-').map((el, i) => {
      if (i > 0) {
        return el.charAt(0).toUpperCase() + el.slice(1).toLowerCase()
      } else {
        return el
      }
    }).join('-')
  } else {
    return str;
  }
}

export function removeSpecialCharactersAtEnd(inputString) {
  // Use a regular expression to match and remove special characters at the end of the string
  if (!inputString) return inputString;

  const aux = inputString.trim();
  return aux.replace(/[^\w\s]$/, '');
}

export function toTitleCaseRails(str) {
  if (str) {
    let lower = str.toLowerCase()
    let re = /\b[a-z]/gi
    let newStr = lower.replace(re, (match) => {
      return match.charAt(0).toUpperCase() + match.slice(1);
    });
    return newStr;

  }
}

export function toTitleCaseExceptState(str) {
  if (str) {
    return toTitleCase(str).replace(/, [A-Z][a-z],/g, state => `, ${state.substring(2, 2).toUpperCase()},`);
  } else {
    return ''
  }
}

export function titleCaseAndCapitalizeExceptions(str, exceptionSize) {
  if (str) {
    const words = str.toLowerCase().split(' ');
    const titleCasedWords = words.map(word => {
      // Check if the word is two letters or less, and if so, keep it lowercase
      if (word.length <= exceptionSize) {
        return word.toUpperCase();
      }

      // Capitalize the first letter of the word
      return word.charAt(0).toUpperCase() + word.slice(1);
    });

    // Join the title-cased words back together with spaces
    const titleCasedString = titleCasedWords.join(' ');

    return titleCasedString;
  } else {
    return ''
  }
}

function startsWithSpecialChars(word) {
  return /^[^\w\d]+/.test(word);
}

function endsWithSpecialChars(word) {
  return /[^\w\d]+$/.test(word);
}

function startsOrEndsWithSpecialChars(word) {
  return startsWithSpecialChars || endsWithSpecialChars
}


export function titleCaseAndIgnoreExceptions(str, exceptionSize) {
  if (str) {
    const words = str.split(' ');
    const resultWords = words.map(word => {
      // Check if the word is of exceptionSize letters or less, and if so, keep it as it is
      if (word.length <= exceptionSize) {
        return word;
      }

      // Check if the word is of exceptionSize and it has an special character attached to it, then keep it as it is
      // example 'TX,' and ',TX' should not be modified (instead of converting it to 'Tx,' ',Tx)
      if (word.length === exceptionSize + 1 && startsOrEndsWithSpecialChars(word)) {
        return word
      }

      // Capitalize the first letter of the word
      return toTitleCase(word)
    });

    // Join the title-cased words back together with spaces
    return resultWords.join(' ');
  } else {
    return ''
  }
}

export const humanizeMoney = (aum, rounded=false) => {
  if (aum) {
    const isNegative = aum < 0;
    aum = Math.abs(aum);
    if (typeof aum === 'string' || aum instanceof String) {
      aum = aum.split(".")[0]
      aum = aum.replace(/[^0-9]/g, '')
    }
    let aumText
    if (aum >= 1000000000000) {
      aumText = rounded ? Math.round(aum / 1000000000000) : (aum / 1000000000000).toFixed(1);
      return `${isNegative ? "-" : ""}$${aumText}T`
    }
    if (aum >= 1000000000) {
      aumText = rounded ? Math.round(aum / 1000000000) : (aum / 1000000000).toFixed(1);
      return `${isNegative ? "-" : ""}$${aumText}B`
    }
    if (aum >= 1000000) {
      aumText = rounded ? Math.round(aum / 1000000) : (aum / 1000000).toFixed(1);
      return `${isNegative ? "-" : ""}$${aumText}M`
    }
    if (aum >= 1000) {
      return `${isNegative ? "-" : ""}$${~~(aum / 1000)}K`
    }
    return `${isNegative ? "-" : ""}$${aum ? aum : 0}`
  }
  return "$0"
}

export const humanizeMoneyCompact = (value) => {
  if (value == null) return 'Undisclosed';
  const num = parseFloat(value);
  if (isNaN(num) || num === 0) return 'Undisclosed';
  const isNegative = value < 0;
  value = Math.abs(value);
  if (typeof value === 'string' || value instanceof String) {
    value = value.split('.')[0];
    value = value.replace(/[^0-9]/g, '');
    value = parseFloat(value);
  }
  const format = (num) => {
    const intPart = Math.floor(num);
    const decimals = intPart >= 100 ? 0 : 1;
    return parseFloat(num.toFixed(decimals)).toString();
  };
  const sign = isNegative ? '-' : '';
  if (value >= 1_000_000_000_000) return `${sign}$${format(value / 1_000_000_000_000)}T`;
  if (value >= 1_000_000_000)     return `${sign}$${format(value / 1_000_000_000)}B`;
  if (value >= 1_000_000)         return `${sign}$${format(value / 1_000_000)}M`;
  if (value >= 1_000)             return `${sign}$${format(value / 1_000)}K`;
  return `${sign}$${value}`;
};

export const humanizeNumberCompact = (value) => {
  if (value == null) return 0;
  const num = parseFloat(value);
  if (isNaN(num) || num === 0) return 0;
  const isNegative = num < 0;
  value = Math.abs(num);
  const format = (n) => {
    const intPart = Math.floor(n);
    const decimals = intPart >= 100 ? 0 : 1;
    return parseFloat(n.toFixed(decimals)).toString();
  };
  const sign = isNegative ? '-' : '';
  if (value >= 1_000_000_000_000) return `${sign}${format(value / 1_000_000_000_000)}T`;
  if (value >= 1_000_000_000)     return `${sign}${format(value / 1_000_000_000)}B`;
  if (value >= 1_000_000)         return `${sign}${format(value / 1_000_000)}M`;
  if (value >= 1_000)             return `${sign}${format(value / 1_000)}K`;
  return `${sign}${numeral(value).format("0,0")}`;
};

export const formatAgeRange = (age) => {
  const ageRange = ['', '20-24', '25-29', '30-34', '35-39', '40-44', '45-49', '50-54', '55-59', '60-64', '65-69', '70-74', '75-79', '80-84', '85-89', '90-94', '95-99', '100'];
  let rangeAge = ''
  if (age !== '') {
    if (parseInt(age) > 99)
      rangeAge = "100+"
    else
      rangeAge = ageRange.filter(it => it.split("-")[0] <= age).pop()
  }
  return rangeAge
}

export const formatBirthYear = (year) => {
  if (!year) return ''
  const age = new Date().getFullYear() - year
  return formatAgeRange(age)
}

export const humanizeNumber = (number) => {
  if (number) {
    if (typeof number === 'string' || number instanceof String) {
      number = number.split(".")[0]
      number = number.replace(/[^0-9]/g, '')
    }
    if (number >= 1000000000000) {
      return `${(number / 1000000000000).toFixed(1)}T`
    }
    if (number >= 1000000000) {
      return `${(number / 1000000000).toFixed(1)}B`
    }
    if (number >= 1000000) {
      return `${(number / 1000000).toFixed(1)}M`
    }
    return numeral(number).format("0,0")
  }
  return 0
}

export const humanizeMonths = (totalMonths, onlyYears = false) => {
  if (totalMonths === 0) return onlyYears ? 'Less than 1 Year' : 'Less than 1 Month';
  if (totalMonths < 0) return '-';

  const years = (totalMonths / 12) | 0;
  const months = (totalMonths % 12) | 0;

  const yearsText = years
    ? `${years} Year${years === 1 ? '' : 's'}`
    : '';

  const monthsText = months
    ? `${months} Month${months === 1 ? '' : 's'}`
    : '';

  return [yearsText, monthsText].filter(Boolean).join(' ');
}


export const numberWithCommas = (number) => {
  return numeral(number).format("0,0")
}

export const retrieveExistingValue = (first, second) => {
  return first ? first : second
}

export const startsWith = (str, prefix) => {
  if (str.length < prefix.length)
    return false;
  for (var i = prefix.length - 1; (i >= 0) && (str[i] === prefix[i]); --i) {
    continue;
  }
  return i < 0;
}

export const endsWith = (str, suffix) => {
  return str.indexOf(suffix, str.length - suffix.length) !== -1;
}


export const numberWithZero = (aum) => {
  return aum < 10 ? `0${aum}` : aum;
}


export const removeNumbersFromString = (str) => {
  return str.replace(/\d+/g, '');
}


export const addMoneySymbol = (aum) => {
  if (aum) {
    aum = aum.toString().replace(/[^0-9]/g, '')
    return `$${aum ? aum : 0}`
  }
  return "$0"
}

export const timeDifference = (start, end, includeDays = true) => {

  const startDate = moment(start)
  const endDate = moment(end)

  var second = 1000,
    minute = 60 * 1000,
    hour = minute * 60,
    day = hour * 24,
    month = day * 30,
    ms = Math.abs(endDate - startDate);

  if (ms < 1500) {
    return [0 + "h", 0 + "m", 1 + "s"].join(":");
  }


  var days = parseInt(ms / day, 10);

  ms -= days * day;

  var hours = parseInt(ms / hour, 10);

  ms -= hours * hour;
  var minutes = parseInt(ms / minute, 10);

  ms -= minutes * minute;

  var seconds = parseInt(ms / second, 10);

  if (includeDays === true) {
    return [days + "d", hours + "h", minutes + "m", seconds + "s"].join(":");
  } else {
    return [hours + "h", minutes + "m", seconds + "s"].join(":");
  }

}


export const addHyphenPermutation = (input) => {
  const number_of_dashes = input.trim().split(' ').length - 1;

  let permutations = [];

  for (let i = 0; i < (1 << number_of_dashes); i++) {
    let boolArray = [];

    for (let j = number_of_dashes - 1; j >= 0; j--) {
      boolArray.push(Boolean(i & (1 << j)));
    }

    permutations.push(boolArray);
  }

  let output = [];
  for (const permutation of permutations) {
    output.push(generatePermutation(permutation, input.trim().split(' ')));
  }

  return output;
}

export const searchKeywordsForHighlight = (searchKeywords) => {
  let keywordsToHighlight = []
  searchKeywords?.filter(item => item.length <= 80).forEach(key => {
    keywordsToHighlight = keywordsToHighlight.concat(addHyphenPermutation(key));
  });
  return keywordsToHighlight;
}

export const keywordHighlight = (searchKeywords) => {
  let moreThanOneWord = false;
  searchKeywords?.forEach(key => {
    if (key.split(' ').length > 1) {
      moreThanOneWord = true;
    }
  });
  if (moreThanOneWord) {
    return searchKeywordsForHighlight(searchKeywords)
  } else {
    return regexKeywordsForHighlight(searchKeywords)
  }
}

export const regexKeywordsForHighlight = (searchKeywords) => {
  let keywordsToHighlight = []
  searchKeywords?.forEach(key => {
    const permutated = addHyphenPermutation(key)
    const regex = new RegExp(`[\\s\\-\\_\\(]${permutated}s?[\\,\\.\\;\\:\\?\\!\\)\\-\\_]*\\s`, 'gi');
    keywordsToHighlight = keywordsToHighlight.concat(regex);
  });
  return keywordsToHighlight;
}

export const validPasswordLength = (password) => {
  return password && (password.length >= 6);
}

export const validPassword = (password) => {
  // This regex checks if string contains 6 to 20 characters, one lowercase, one uppercase and one digit
  const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{6,20}$/;
  return password && !!password.match(passwordRegex);
}

export const validEmail = (email) => {
  // This regex checks for valid email format
  const emailRegex = /^[\w+\-.'’"]+@[a-z\d\-]+(\.[a-z\d\-]+)*\.[a-z]{2,}$/i
  return email && !!email.match(emailRegex);
}

export const validUrl = (url) => {
  const urlRegex = /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i;
  return url && !!url.match(urlRegex);
}

export const validPhoneNumber = (phone) => {
  const phoneRegex = /^[+\-0-9(). ]+$/gi
  return phone && !!phone.match(phoneRegex);
}

export const validLinkedInUrl = (url) => {
  if (url) {
    const urlRegex = /^https:\/\/www\.linkedin\.com\/in\/[^\s!#$&'()*+,\/:;=?@[\] ".<>\\^_`{|}´]+[\/]{0,1}$/gm;
    return urlRegex.test(url);
  }
  return true;
}

export const validLinkedInCompanyUrl = (url) => {
  if (url) {
    const urlRegex = /^https:\/\/((www|\w\w)\.)?linkedin\.com\/(in|company)\/[\s!#$&'()*+,\/:;=?@".<>\\^_`{|}´\-\w]+\/?/gm;
    return urlRegex.test(url);
  }
  return true;
}

export const validDate = (date) => {
  let today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(date);
  return d > today;
}

export const mapIds = (elements) => {
  return elements.map(({ id }) => id);
}

export const generateArrayOfYears = (amount) => {
  const max = new Date().getFullYear();
  const min = max - amount + 1
  const years = []

  for (let i = max; i >= min; i--) {
    years.push(i)
  }
  return years
}

export const timeElapsed = (start, end) => {
  const result = intervalToDuration({
    start: start,
    end: end
  })

  if (result.years !== 0) {
    if (result.years === 1) {
      return 'a year'
    } else {
      return `${result.years} years`
    }
  }
  if (result.months !== 0) {
    return _partialMessage(result.months, result.days, 'months', 'days')
  }

  if (result.days !== 0) {
    return _partialMessage(result.days, result.hours, 'days', 'hours')
  }

  if (result.hours !== 0) {
    return _partialMessage(result.hours, result.minutes, 'hours', 'minutes')
  }

  if (result.minutes !== 0) {
    return _partialMessage(result.minutes, result.seconds, 'minutes', 'seconds')
  }

  return ''
}

export const getRandomColor = () => {
  const letters = '0123456789ABCDEF';
  let color = '#';
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
}

export const fixedPercentage = (total, portion, decimals) => {
  let result = 0
  if (total > 0) {
    result = portion * 100 / total
  }
  if (!Number.isInteger(result)) {
    result = result.toFixed(decimals)
  }
  return `${result}%`
}

export const customPropertyDisplay = (name, userId, customProperty) => {
  return `${name} ${(userId !== customProperty.user_id) && (customProperty.user_identifier) ? `(${customProperty.user_identifier})` : ''}`;
}

export const calcArrayLength = (arr) => {
  return Array.isArray(arr) ? arr.length : 0
}

const _partialMessage = (firstValue, secondValue, firstUnit, secondUnit) => {
  if (firstValue === 1) {
    const singleUnit = firstUnit.replace('s', '')
    if (secondValue < 1) {
      return `a ${singleUnit}`
    } else {
      return `a ${singleUnit} ${secondValue} ${secondUnit}`
    }
  } else {
    if (secondValue < 1) {
      return `${firstValue} ${firstUnit}`
    } else {
      return `${firstValue} ${firstUnit} ${secondValue} ${secondUnit}`
    }
  }
}

export const transformIDsToHash = (idsArray) => {
  const idsHash = {};

  if (Array.isArray(idsArray)) {
    idsArray.forEach((id, index) => {
      idsHash[index] = id;
    });
  }

  return idsHash;
}

export const validateExceedsMax = (value, maxValue, add = 0) => {
  const parsedValue = parseInt(value, 10);
  const adjustedMax = Math.max(0, maxValue) + (isNaN(add) ? 0 : add);

  if (value !== '' && parsedValue > adjustedMax) {
    return 'Exceeds the maximum allowed';
  }
  return '';
};

export const entityToDisplay = {
  "investors": "investors",
  "Investor": "investors",
  "entities": "firms",
  "RiaInvestor": "firms",
  "contacts": "contacts",
  "Contact": "contacts",
  "reps": "reps",
  "RiaContact": "reps",
  "teams": "teams",
  "PrivateWealthTeam": "teams",
  "Foundation": "entities",
  "foundations": "entities",
  "FoundationContact": "contacts",
  "foundation_contacts": "contacts",
    "bt_entities": "entities",
    "BankTrust": "entities",
    "bank_trusts": "entities",
    "bt_contacts": "contacts",
    "BtContact": "contacts",
}

export const associatedEntityToDisplay = {
  "investors": "contacts",
  "Investor": "contacts",
  "entities": "reps",
  "RiaInvestor": "reps",
  "contacts": "investors",
  "Contact": "investors",
  "reps": "firms",
  "RiaContact": "firms",
  "teams": "reps",
  "PrivateWealthTeam": "reps",
  "Foundation": "contacts",
  "foundations": "contacts",
  "FoundationContact": "entities",
  "foundation_contacts": "entities",
    "bt_entities": "contacts",
    "BankTrust": "contacts",
    "bank_trusts": "contacts",
    "bt_contacts": "entities",
    "BtContact": "entities",
}


export const shortMonth = {
  "1": "Jan",
  "2": "Feb",
  "3": "Mar",
  "4": "Apr",
  "5": "May",
  "6": "Jun",
  "7": "Jul",
  "8": "Aug",
  "9": "Sep",
  "10": "Oct",
  "11": "Nov",
  "12": "Dec",
}

export const entityTypeFormatter = {
  'RIA': 'Registered Investment Advisor',
  'Ria': 'Registered Investment Advisor',
  'ria': 'Registered Investment Advisor',
  'ERA': 'Exempt Reporting Advisor',
  'Era': 'Exempt Reporting Advisor',
  'era': 'Exempt Reporting Advisor',
  'Bd': 'Broker Dealer',
  'BD': 'Broker Dealer',
  'bd': 'Broker Dealer',
  'DR': 'Reg. Investment Advisor & Broker Dealer',
  'Dr': 'Reg. Investment Advisor & Broker Dealer',
  'dr': 'Reg. Investment Advisor & Broker Dealer',
}

export const institutionTypeFormatter = {
    'bank': 'Bank',
    'trust': 'Trust',
}

export const AvgAccountSizeMax = 250000000
export const assetMax = 5000000000
export const assetUMMax = 20000000000

export const investmentMax = 2000000000
export const aumMax = 2000000000

export const assetStep = 50000000
export const OneMillionStep = 1000000
export const TwentyMillionStep = 20000000

export const accountMax = 1000
export const accountStep = 10
export const FiveStep = 5

export const employeesMax = 150
export const employeesStep = 50

export const portfolioFinderMax = 5000000000
export const portfolioGrowthMax = 1000000000
export const portfolioStep = 1000000

export const ADMIN_FILTER_MAX_CONNECTIONS = 1_000
export const ADMIN_FILTER_CONNECTIONS_STEP = 1

export const getItemListType = (value) => {
  switch (value) {
    case 'ria_prospects':
      return 'RiaList';
    case 'reps_dossier':
      return 'RiaList';
    case 'entities_dossier':
      return 'RiaList';
    case 'pwt_prospects':
      return 'PwtList';
    case 'ria_dynamic':
      return 'RiaList';
    case 'pwt_dynamic':
      return 'PwtList';
    case 'pwt_dynamic_filtered':
      return 'PwtList';
    case 'ria_distribution':
      return 'RiaList';
  }
}

export const getItemListTypeName = (value) => {
  switch (value) {
    case 'ria_prospects':
      return 'Static List';
    case 'ria_dynamic':
      return 'Dynamic List';
    case 'ria_distribution':
      return 'Master Selling Agreement';
  }
}

export const getRepsSuffix = (typeList) => {
  if (typeList === 'pwt_dynamic_filtered') {
    return '(Filtered Reps Only)';
  }

  if (typeList === 'pwt_dynamic' || typeList === 'pwt_prospects') {
    return '(All Associated Reps)';
  }

  return '';
};


export const hasTaskOnBulkAllow = {
  'areebah@plutus21.com': true,
  'ayesha@plutus21.com': true,
  'danyaal@plutus21.com': true,
  'hamiz@plutus21.com': true,
  'narmeen@plutus21.com': true,
  'richard@plutus21.com': true,
  'parker.hall@fintrx.com': true,
  'parker.hall@capitalhedge.net': true,
}

export const uspsStates = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY'
]

export const stateCodeToName = {
  'AL': 'Alabama',
  'AK': 'Alaska',
  'AZ': 'Arizona',
  'AR': 'Arkansas',
  'CA': 'California',
  'CO': 'Colorado',
  'CT': 'Connecticut',
  'DE': 'Delaware',
  'FL': 'Florida',
  'GA': 'Georgia',
  'HI': 'Hawaii',
  'ID': 'Idaho',
  'IL': 'Illinois',
  'IN': 'Indiana',
  'IA': 'Iowa',
  'KS': 'Kansas',
  'KY': 'Kentucky',
  'LA': 'Louisiana',
  'ME': 'Maine',
  'MD': 'Maryland',
  'MA': 'Massachusetts',
  'MI': 'Michigan',
  'MN': 'Minnesota',
  'MS': 'Mississippi',
  'MO': 'Missouri',
  'MT': 'Montana',
  'NE': 'Nebraska',
  'NV': 'Nevada',
  'NH': 'New Hampshire',
  'NJ': 'New Jersey',
  'NM': 'New Mexico',
  'NY': 'New York',
  'NC': 'North Carolina',
  'ND': 'North Dakota',
  'OH': 'Ohio',
  'OK': 'Oklahoma',
  'OR': 'Oregon',
  'PA': 'Pennsylvania',
  'RI': 'Rhode Island',
  'SC': 'South Carolina',
  'SD': 'South Dakota',
  'TN': 'Tennessee',
  'TX': 'Texas',
  'UT': 'Utah',
  'VT': 'Vermont',
  'VA': 'Virginia',
  'WA': 'Washington',
  'WV': 'West Virginia',
  'WI': 'Wisconsin',
  'WY': 'Wyoming',
  'DC': 'District of Columbia',
}

export const getStateNameByCode = (code) => {
  if (!code) return code;
  return stateCodeToName[code.toUpperCase()] || code;
}

export const marksMiles = [
  {
    value: 5,
    label: '5 Miles',
  },
  {
    value: 100,
    label: '100 Miles',
  },
]

export const monthNames = [
  null,
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];

export const badEmails = [
  "sales@",
  "marketing@",
  "media@",
  "content@",
  "investment@",
  "info@",
  "contact@"
];

export const usRegionOptions = [
  { name: 'East', id: 'East', states: ['DE', 'FL', 'GA', 'KY', 'MD', 'NC', 'SC', 'TN', 'VA', 'WV', 'DC', 'NJ', 'NY', 'PA', 'CT', 'MA', 'ME', 'RI', 'VT', 'NH'] },
  { name: 'Midwest', id: 'Midwest', states: ['IA', 'IL', 'IN', 'KS', 'MI', 'MN', 'MO', 'ND', 'NE', 'OH', 'SD', 'WI'] },
  { name: 'New England', id: 'New England', states: ['CT', 'MA', 'ME', 'NH', 'RI', 'VT'] },
  { name: 'Northeast', id: 'Northeast', states: ['DE', 'MD', 'NJ', 'NY', 'PA', 'DC', 'CT', 'MA', 'ME', 'NH', 'RI', 'VT'] },
  { name: 'Northwest', id: 'Northwest', states: ['ID', 'OR', 'WA'] },
  { name: 'Southeast', id: 'Southeast', states: ['FL', 'GA', 'KY', 'NC', 'SC', 'TN', 'VA'] },
  { name: 'Southwest', id: 'Southwest', states: ['TX', 'OK', 'AZ', 'NM'] },
  { name: 'West', id: 'West', states: ['CO', 'ID', 'MT', 'NV', 'WY', 'CA', 'HI', 'OR', 'WA', 'TX', 'AZ', 'NM', 'UT', 'OK'] },
  { name: 'South', id: 'South', states: ['AL', 'AR', 'LA', 'MS', 'TX', 'OK', 'AZ', 'NM'] },
];


export const familyOfficeTypeAbbreviator = {
  'multi family office': 'MFO',
  'single family office': 'SFO',
  'no family office': 'NFO',
}

export const familyOfficeTypes = {
  'MFO': 'Multi Family Office',
  'SFO': 'Single Family Office',
  'NFO': 'No Family Office',
}

export function compactNumberNotation(labelValue) {
  // Nine Zeroes for Billions
  return Math.abs(Number(labelValue)) >= 1.0e+9

    ? Math.abs(Number(labelValue)) / 1.0e+9 + "B"
    // Six Zeroes for Millions
    : Math.abs(Number(labelValue)) >= 1.0e+6

      ? Math.abs(Number(labelValue)) / 1.0e+6 + "M"
      // Three Zeroes for Thousands
      : Math.abs(Number(labelValue)) >= 1.0e+3

        ? Math.abs(Number(labelValue)) / 1.0e+3 + "K"

        : Math.abs(Number(labelValue));
}

export const showIsNewUntil = (endDate) => {
  return moment(endDate).isSameOrAfter(moment().format('YYYY-MM-DD'), 'day');
};


export const calculateHtmlAndImages = async (node) => {
  const findSvgs = node.querySelectorAll('svg')
  const images = []
  for (let i = 0; i < findSvgs.length; i++) {
    const id = 'chart-' + i
    const base64 = await convertSVGtoImgBase64(findSvgs[i])
    images.push({ base64: base64.replace('data:image/png;base64,', ''), id: id })
    const img = new Image()
    img.id = id
    findSvgs[i].parentNode.insertBefore(img, findSvgs[i])
    findSvgs[i].remove()
  }

  return { html: node.innerHTML, images }
}

export const convertSVGtoImgBase64 = (svg) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const img = new Image(550, 393);
  const svgData = new XMLSerializer().serializeToString(svg);
  const svg64 = btoa(svgData);

  const b64Start = 'data:image/svg+xml;base64,';
  const image64 = b64Start + svg64;
  img.src = image64;

  return new Promise((resolve, reject) => {
    img.onload = () => {
      canvas.width = img.width
      canvas.height = img.height
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    }
  })

}

export const formatEinNum = (einNum) => {
  if (!einNum) {
    return einNum;
  }

  const einString = einNum.toString();
  const amountOfZeros = 9 - einString.length;
  return `${Array(amountOfZeros + 1).join("0")}${einString}`;
}

export const getAvatarUrl = (el) => {
  if (_.isString(el?.photo))
    return el.photo

  if (_.isString(el?.photo?.url))
    return el.photo.url

  if (_.isString(el?.photo_url))
    return el.photo_url

  return null
}

export const obfuscateString = (string, showCharacters=4) => {
  if (_.isEmpty(string)) {
    return string
  }

  const characters = string.length
  if (characters < showCharacters) {
    return string
  }

  const obfuscatedSection = "*".repeat(string.length - showCharacters)
  if (showCharacters === 0) {
    return obfuscatedSection
  }

  return obfuscatedSection + string.slice(string.length - showCharacters)
}

export const extractIds = (filters) => {
  return Object.entries(filters).reduce((acc, [key, value]) => {
    if (Array.isArray(value)) {
      acc[key] = value.map(item => item.id);
    }
    return acc;
  }, {});
}


export const entityToModel = {
  "investors": "Investor",
  "entities": "RiaInvestor",
  "contacts": "Contact",
  "reps": "RiaContact",
}

export const removeKeysBySubstrings = (obj = {}, substrings = []) =>
    Object.fromEntries(
        Object.entries(obj).filter(
            ([key]) => !substrings.some(sub => key.includes(sub))
        )
    );

export const getEntityDisplayName = (entityType) => {
  if (!entityType) {
    return "";
  }

  const ENTITY_TYPE_MAP = {
    "RiaInvestor": "firm",
    "RiaContact": "rep",
    "Investor": "investor",
    "Contact": "contact",
    "WealthTeam": "team",
  };
  return ENTITY_TYPE_MAP[entityType] || entityType.toLowerCase();
};

export const urlSlugToText = (url) => {
  try {
    const { pathname } = new URL(url);
    const segments = pathname.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1] || '';
    // Remove extension
    const withoutExt = lastSegment.replace(/\.[^.]+$/, '');
    // Split by hyphen, remove trailing all-numeric parts (e.g. ids/timestamps)
    const parts = withoutExt.split('-');
    const meaningfulParts = parts.filter((part, i) => {
      const isTrailingNumeric = /^\d+$/.test(part) && i === parts.length - 1;
      return !isTrailingNumeric;
    });
    const text = meaningfulParts.join(' ');
    // Sentence case: capitalize only the first letter
    return text.charAt(0).toUpperCase() + text.slice(1);
  } catch {
    return url;
  }
};



export class fuzzyMatchWindow{
  static low = 0;
  static medium = 50;
  static high = 75;
}
